import { createContext, use, useMemo, useId, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';
import { inspectorInstance, Avo } from '@iwt-core-web/avo';

const TrackingProviderContext = createContext(null);
const useTrackingProvider = ()=>{
    const context = use(TrackingProviderContext);
    if (context === null) {
        throw new Error("`useTrackingProvider` must be used within a `TrackingProvider`");
    }
    return context;
};

/**
 * Manages and provides global context to applications.
 *
 * Supplies the event brand and user-consent status through React context.
 */ const TrackingProvider = ({ brand, children, hasUserConsent })=>{
    const contextValue = useMemo(()=>({
            brand,
            hasUserConsent
        }), [
        brand,
        hasUserConsent
    ]);
    return /*#__PURE__*/ jsx(TrackingProviderContext, {
        value: contextValue,
        children: children
    });
};

const scopes = new Map();
let activeScopeId = null;
/** Plugins own their credentials, so Avo destinations are made without an api key. */ const NO_API_KEY = "";
const CustomDestination = {
    make: ()=>{},
    logEvent: (eventName, eventProperties)=>{
        if (activeScopeId === null) {
            return;
        }
        const scope = scopes.get(activeScopeId);
        if (scope === undefined) {
            return;
        }
        // `component` is a legacy alias for `appId` and should not reach plugins.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { component, ...restEventProperties } = eventProperties;
        const scopedProperties = {
            ...restEventProperties,
            brand: scope.brand,
            appId: scope.appId
        };
        for (const plugin of scope.plugins){
            plugin.logEvent?.(eventName, scopedProperties);
        }
    }
};
const registerTrackingScope = (id, brand, appId, env, plugins)=>{
    scopes.set(id, {
        brand,
        appId,
        plugins
    });
    for (const plugin of plugins){
        plugin.make?.(env, NO_API_KEY);
    }
    return ()=>{
        scopes.delete(id);
    };
};
const withTrackingScope = (id, callback)=>{
    const previousScopeId = activeScopeId;
    activeScopeId = id;
    try {
        return callback();
    } finally{
        activeScopeId = previousScopeId;
    }
};

const TrackingScopeContext = createContext(null);
const useTrackingScope = ()=>{
    const context = use(TrackingScopeContext);
    if (context === null) {
        throw new Error("`useTrackingScope` must be used within a `TrackingScope`");
    }
    return context;
};

const EMPTY_PLUGINS = [];
/**
 * Avo is initialized globally, so scopes mounted with conflicting environments
 * overwrite each other. Tracked to surface the conflict instead of silently
 * letting the last mounted scope win.
 */ let initializedAvoEnv = null;
const pluginKeys = new WeakMap();
let pluginCount = 0;
/** Stable string identity for a plugin, falling back to a per-object token. */ const getPluginKey = (plugin)=>{
    if (plugin.id) {
        return plugin.id;
    }
    let key = pluginKeys.get(plugin);
    if (key === undefined) {
        pluginCount += 1;
        key = `plugin-${pluginCount}`;
        pluginKeys.set(plugin, key);
    }
    return key;
};
const dedupePlugins = (plugins)=>{
    const loadedPlugin = new Map();
    for (const plugin of plugins){
        if (plugin === undefined) continue;
        const key = getPluginKey(plugin);
        if (!loadedPlugin.has(key)) {
            loadedPlugin.set(key, plugin);
        }
    }
    return Array.from(loadedPlugin.values());
};
const TrackingScope = (props)=>{
    const { env, plugins = EMPTY_PLUGINS, children, appId: appIdProp, component } = props;
    const appId = appIdProp ?? component;
    if (!appId) {
        throw new Error("`TrackingScope` requires either `appId` or `component` prop");
    }
    const resolvedAppId = appId;
    const scopeId = useId();
    const hasInitializedAvoRef = useRef(false);
    const { brand, hasUserConsent } = useTrackingProvider();
    useEffect(()=>{
        if (component && !appIdProp) {
            console.warn("`TrackingScope` component prop is deprecated. Use `appId` instead.");
        }
    }, [
        component,
        appIdProp
    ]);
    /**
   * Keyed by plugin identity rather than by the `plugins` array reference, so an
   * inline array literal does not re-register the scope on every render.
   */ const pluginsKey = plugins.map((plugin)=>plugin === undefined ? "" : getPluginKey(plugin)).join("|");
    const pluginsCacheRef = useRef(null);
    if (pluginsCacheRef.current?.key !== pluginsKey) {
        pluginsCacheRef.current = {
            key: pluginsKey,
            plugins: dedupePlugins(plugins)
        };
    }
    const configuredPlugins = pluginsCacheRef.current.plugins;
    const activePlugins = useMemo(()=>configuredPlugins.filter((plugin)=>!plugin.isUserConsentRequired || hasUserConsent), [
        configuredPlugins,
        hasUserConsent
    ]);
    useLayoutEffect(()=>{
        let isActive = true;
        const unregister = registerTrackingScope(scopeId, brand, resolvedAppId, env, activePlugins);
        const initializeAvo = (inspector)=>{
            if (!isActive || hasInitializedAvoRef.current) return;
            if (initializedAvoEnv !== null && initializedAvoEnv !== env) {
                console.warn(`TrackingScope "${resolvedAppId}" is initializing Avo with env "${env}", but env "${initializedAvoEnv}" is already active. Because Avo is configured globally, the most recently mounted scope takes precedence.`);
            }
            initializedAvoEnv = env;
            hasInitializedAvoRef.current = true;
            Avo.initAvo(// 1. Avo init options
            {
                env,
                webDebugger: env === Avo.AvoEnv.Dev,
                ...process.env.ENVIRONMENT === "live" && inspector !== undefined ? {
                    inspector
                } : {}
            }, // 2. Destination options (pass an empty object if you don't have specific config)
            {}, // 3. Route generated Avo events through the active TrackingScope.
            CustomDestination);
        };
        if (process.env.ENVIRONMENT === "live") {
            void inspectorInstance.then(initializeAvo);
        } else {
            initializeAvo();
        }
        return ()=>{
            isActive = false;
            unregister();
        };
    }, [
        activePlugins,
        brand,
        resolvedAppId,
        env,
        scopeId,
        component
    ]);
    const contextValue = useMemo(()=>({
            appId: resolvedAppId,
            scopeId
        }), [
        resolvedAppId,
        scopeId
    ]);
    return /*#__PURE__*/ jsx(TrackingScopeContext, {
        value: contextValue,
        children: children
    });
};

/**
 * Invokes a tracking event function with its typed properties.
 *
 * Uses a type assertion to bypass TypeScript's limitation in preserving
 * the relationship between event function unions and their corresponding
 * property types. The assertion scope is confined here to maintain type
 * safety at the call site.
 */ const dispatchEvent = (event)=>{
    event.fn(event.props);
};
const useEventFactory = ()=>{
    useTrackingProvider();
    const { scopeId } = useTrackingScope();
    return useCallback(async (input)=>{
        const normalized = Array.isArray(input) ? input : [
            input
        ];
        normalized.forEach((event)=>{
            withTrackingScope(scopeId, ()=>dispatchEvent(event));
        });
    }, [
        scopeId
    ]);
};

/**
 * Hook for tracking action events.
 *
 * @returns A callback function that, when invoked, will dispatch all associated action events
 */ const useActionEvent = ()=>{
    return useEventFactory();
};

/**
 * A hook for tracking pageView events.
 *
 * @param path - The current path of the page being viewed (it can also be an identifier if the current view has no dedicated path)
 * @param events - A single or array of { fn, props } event objects to fire on page view
 * @returns void - Events are automatically dispatched when path changes
 */ const usePageViewEvent = (path, events)=>{
    const eventFactory = useEventFactory();
    /**
   * Ref is used to set and retrieve the latest value without impacting the React rendering lifecycle.
   * It avoids extra hook dependency that could lead to unwanted duplicate event submission
   */ const latestRef = useRef({
        eventFactory,
        events
    });
    useEffect(()=>{
        latestRef.current = {
            events,
            eventFactory
        };
    }, [
        eventFactory,
        events
    ]);
    useEffect(()=>{
        const { eventFactory, events } = latestRef.current;
        eventFactory(events);
    }, [
        path
    ]);
};

const THRESHOLD = 0;
const ROOT_MARGIN = "0px";
/**
 * Hook for tracking an element once it enters the viewport.
 * Definition of viewed reference - https://avivgroup.atlassian.net/wiki/spaces/DATA/pages/1443660005/Classifieds+Analytics+Glossary#Viewed-classified-(card)
 *
 * @param eventData - A `{ fn, props }` event object or array to send when the element becomes visible
 * @returns An object containing a ref to attach to the element being tracked
 *
 * @example
 * ```typescript
 * const { ref } = useElementViewEvent({ fn: viewPage, props: pageProperties });
 *
 * return <div ref={ref}>Content to track</div>;
 * ```
 */ const useElementViewEvent = (eventData)=>{
    const trackEvent = useEventFactory();
    const hasTriggeredRef = useRef(false);
    const observerRef = useRef(null);
    const eventDataRef = useRef(eventData);
    useEffect(()=>{
        eventDataRef.current = eventData;
    }, [
        eventData
    ]);
    const ref = useCallback((node)=>{
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!node || hasTriggeredRef.current || typeof window === "undefined" || !("IntersectionObserver" in window)) return;
        const observer = new IntersectionObserver(([entry], observer)=>{
            if (entry?.isIntersecting && !hasTriggeredRef.current) {
                trackEvent(eventDataRef.current);
                observer.disconnect();
                observerRef.current = null;
                hasTriggeredRef.current = true;
            }
        }, {
            threshold: THRESHOLD,
            root: null,
            rootMargin: ROOT_MARGIN
        });
        observerRef.current = observer;
        observer.observe(node);
    }, [
        trackEvent
    ]);
    useEffect(()=>{
        return ()=>{
            observerRef.current?.disconnect();
        };
    }, []);
    return {
        ref
    };
};

const createPlugin = (input)=>{
    // Dumb factory currently used now, could be improved when needed.
    return input;
};

/**
 * Builds an event envelope factory with generated `id`, `time` and `specversion`.
 *
 * @deprecated Superseded by generated Avo event functions dispatched through the
 * event hooks. Kept for the Google Analytics event builders until they are removed.
 */ const createEvent = (input)=>{
    return (providerEventMetadata)=>{
        const eventMetadata = {
            ...providerEventMetadata,
            ...input.metadata,
            id: crypto.randomUUID(),
            time: new Date().toISOString(),
            specversion: "1.0"
        };
        return {
            metadata: eventMetadata,
            payload: input.createPayload(eventMetadata)
        };
    };
};

export { TrackingProvider, TrackingScope, createEvent, createPlugin, useActionEvent, useElementViewEvent, usePageViewEvent };
